import React, { useMemo } from "react";

/**
 * Detect YouTube / TikTok / direct-video URLs and return a structured embed descriptor.
 * Returns null if the url doesn't match a known embeddable host.
 */
export function detectEmbed(url) {
  if (!url) return null;
  try {
    const u = new URL(url.startsWith("http") ? url : `https://${url}`);
    const host = u.hostname.replace(/^www\./, "");

    // YouTube — youtube.com/watch?v=..., youtu.be/..., youtube.com/shorts/...
    if (host === "youtube.com" || host === "m.youtube.com" || host === "music.youtube.com") {
      const v = u.searchParams.get("v");
      if (v) return { kind: "youtube", id: v, url };
      const shorts = u.pathname.match(/^\/shorts\/([A-Za-z0-9_-]{5,})/);
      if (shorts) return { kind: "youtube", id: shorts[1], url };
      const embed = u.pathname.match(/^\/embed\/([A-Za-z0-9_-]{5,})/);
      if (embed) return { kind: "youtube", id: embed[1], url };
    }
    if (host === "youtu.be") {
      const id = u.pathname.replace(/^\//, "").split("?")[0];
      if (id) return { kind: "youtube", id, url };
    }

    // TikTok — www.tiktok.com/@user/video/ID, vm.tiktok.com/ID
    if (host === "tiktok.com") {
      const m = u.pathname.match(/\/video\/(\d{6,})/);
      if (m) return { kind: "tiktok", id: m[1], url };
    }
    if (host === "vm.tiktok.com" || host === "vt.tiktok.com") {
      // Short-link form — we'll fall back to oEmbed link card
      return { kind: "tiktok", id: u.pathname.replace(/^\//, ""), url, isShort: true };
    }

    // Direct video files
    if (/\.(mp4|webm|ogg|mov|m4v)$/i.test(u.pathname)) {
      return { kind: "video", url };
    }
  } catch (e) { /* ignore */ }
  return null;
}

function YouTubeEmbed({ id, url }) {
  const src = `https://www.youtube-nocookie.com/embed/${id}`;
  const thumb = `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer nofollow"
      className="block border border-[#E4E4E7] dark:border-[#2A2A30] overflow-hidden bg-[#F4F4F5] dark:bg-[#232329] hover:border-[#7C3AED] transition-colors"
      data-testid="yt-embed"
    >
      <div className="relative aspect-video bg-black">
        <iframe
          src={src}
          title="YouTube video"
          loading="lazy"
          referrerPolicy="strict-origin-when-cross-origin"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          className="absolute inset-0 w-full h-full border-0"
        />
      </div>
      <div className="px-3 py-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A]">
        <img src={thumb} alt="" className="w-6 h-4 object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        YouTube · {new URL(url.startsWith("http") ? url : `https://${url}`).hostname.replace(/^www\./, "")}
      </div>
    </a>
  );
}

function TikTokEmbed({ id, url, isShort }) {
  // TikTok's iframe embed URL
  const src = isShort ? null : `https://www.tiktok.com/embed/v2/${id}`;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer nofollow"
      className="block border border-[#E4E4E7] dark:border-[#2A2A30] overflow-hidden bg-[#F4F4F5] dark:bg-[#232329] hover:border-[#7C3AED] transition-colors"
      data-testid="tiktok-embed"
    >
      {src ? (
        <div className="relative" style={{ aspectRatio: "9 / 16", maxHeight: "640px" }}>
          <iframe
            src={src}
            title="TikTok video"
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="absolute inset-0 w-full h-full border-0"
          />
        </div>
      ) : (
        <div className="px-4 py-6 text-center">
          <div className="font-heading text-lg uppercase tracking-wider text-[#09090B] dark:text-[#F4F4F5]">Open on TikTok →</div>
          <div className="font-mono text-[10px] text-[#71717A] mt-1">Tap to watch this clip</div>
        </div>
      )}
      <div className="px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A]">TikTok</div>
    </a>
  );
}

function DirectVideo({ url }) {
  return (
    <video
      src={url}
      controls
      playsInline
      preload="metadata"
      className="w-full max-h-[480px] bg-black border border-[#E4E4E7] dark:border-[#2A2A30]"
      data-testid="direct-video-embed"
    />
  );
}

/**
 * Given text, extract every URL, return the FIRST embeddable one. We only render
 * one embed per post to keep the feed tidy. Returns null if no embeddable found.
 */
export function firstEmbed(text) {
  if (!text) return null;
  const URL_RE = /\b((?:https?:\/\/|www\.)[^\s<>"]+)/gi;
  let m;
  while ((m = URL_RE.exec(text)) !== null) {
    const e = detectEmbed(m[1]);
    if (e) return e;
  }
  return null;
}

/**
 * Render an embed descriptor.
 */
export default function LinkEmbed({ text }) {
  const embed = useMemo(() => firstEmbed(text), [text]);
  if (!embed) return null;
  if (embed.kind === "youtube") return <YouTubeEmbed id={embed.id} url={embed.url} />;
  if (embed.kind === "tiktok")  return <TikTokEmbed id={embed.id} url={embed.url} isShort={embed.isShort} />;
  if (embed.kind === "video")   return <DirectVideo url={embed.url} />;
  return null;
}
